import { jwtDecode } from 'jwt-decode';
import {
 createContext,
 ReactNode,
 useCallback,
 useContext,
 useEffect,
 useMemo,
 useState,
} from 'react';
import { getCookieCustom } from '../utils/cookies';
import { AUTH_TOKENS_PERSISTED_EVENT } from '../utils/authTokens';
import {
 AuthUser,
 AccessGroupsDTO,
 GroupWithRolesDTO,
 RolesDTO,
 UserDatabasesDTO,
 UsersDTO,
} from '../redux/identity/identityTypes';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
import {
 usersGetSelfAccessGroupsStart,
 usersGetSelfStart,
} from '@/redux/users/usersSlice';

export type AuthContextValue = {
 loginUser: AuthUser | null;
 selfUser: UsersDTO | null;
 isAuthenticated: boolean;
 userRoles: string[];
 hasRole: (resource: string, action: string) => boolean;
 databases: { Id: string; Client: string }[] | null;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const SELF_USER_STORAGE_KEY = 'selfUser';
const ACCESS_GROUPS_STORAGE_KEY = 'accessGroups';
const USER_ROLES_STORAGE_KEY = 'userRoles';

const getDecodedLoginUserFromToken = () => {
 const accessTokenCookie = getCookieCustom('access_token');
 if (!accessTokenCookie) {
  return null;
 }
 try {
  const decoded = jwtDecode(accessTokenCookie) as AuthUser;
  return decoded;
 } catch (error) {
  console.error('Failed to decode access token', error);
  return null;
 }
};

const getDatabasesFromLoginUser = (loginUser: AuthUser | null) => {
 return (
  loginUser?.databases.map((d) => {
   const item = d.split(':');
   return {
    Id: item[0],
    Client: item[1],
   };
  }) || []
 );
};

const getDatabasesFromSelfUser = (
 getDatabasesFromSelfUser: UsersDTO | null,
): { Id: string; Client: string }[] | null => {
 if (
  !Array.isArray(getDatabasesFromSelfUser?.Databases) ||
  getDatabasesFromSelfUser.Databases.length === 0
 ) {
  return null;
 }

 return getDatabasesFromSelfUser.Databases.map((database: UserDatabasesDTO) => {
  return {
   Id: database.Id?.toString() || '',
   Client: database.Client || '',
  };
 });
};

const getUserRolesFromAccessGroups = (
 accessGroups: (GroupWithRolesDTO | AccessGroupsDTO)[] | null | undefined,
): string[] => {
 if (!Array.isArray(accessGroups)) {
  return [];
 }

 const rolesSet = new Set<string>();

 accessGroups.forEach((group) => {
  if (!('Roles' in group) || !Array.isArray(group.Roles)) {
   return;
  }

  group.Roles.forEach((role: RolesDTO | null | undefined) => {
   if (!role?.Name || !role?.Action) {
    return;
   }

   const roleKey = `${role.Name}.${role.Action}`.toLowerCase();
   rolesSet.add(roleKey);
  });
 });

 return Array.from(rolesSet);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
 const dispatch = useAppDispatch();
 const { user, accessGroups, success } = useAppSelector(
  (state: RootState) => state.users,
 );

 const [loginUser, setLoginUser] = useState<AuthUser | null>(() =>
  getDecodedLoginUserFromToken(),
 );
 const [selfUser, setSelfUser] = useState<UsersDTO | null>(() =>
  localStorage.getItem(SELF_USER_STORAGE_KEY)
   ? JSON.parse(localStorage.getItem(SELF_USER_STORAGE_KEY) || '')
   : null,
 );
 const [userRoles, setUserRoles] = useState<string[]>(() => {
  const storedUserRoles = localStorage.getItem(USER_ROLES_STORAGE_KEY);
  if (!storedUserRoles) {
   return [];
  }

  try {
   const parsedUserRoles = JSON.parse(storedUserRoles);

   if (
    Array.isArray(parsedUserRoles) &&
    parsedUserRoles.every((value) => typeof value === 'string')
   ) {
    return parsedUserRoles;
   }

   return [];
  } catch {
   return [];
  }
 });

 const hasRole = useCallback(
  (resource: string, action: string) => {
   if (!resource || !action) {
    return false;
   }

   if (!Array.isArray(userRoles) || userRoles.length === 0) {
    return false;
   }

   const requiredKey = `${resource}.${action}`.toLowerCase();

   return userRoles.some((role) => role.toLowerCase() === requiredKey);
  },
  [userRoles],
 );

 useEffect(() => {
  const handleAuthTokensPersisted = () => {
   const nextLoginUser = getDecodedLoginUserFromToken();
   setLoginUser(nextLoginUser);
   if (!nextLoginUser) {
    return;
   }
   localStorage.removeItem(ACCESS_GROUPS_STORAGE_KEY);
   localStorage.removeItem(USER_ROLES_STORAGE_KEY);
   setUserRoles([]);
  };

  window.addEventListener(AUTH_TOKENS_PERSISTED_EVENT, handleAuthTokensPersisted);
  return () => {
   window.removeEventListener(
    AUTH_TOKENS_PERSISTED_EVENT,
    handleAuthTokensPersisted,
   );
  };
 }, []);

 useEffect(() => {
  if (!loginUser) {
   setSelfUser(null);
   localStorage.removeItem(SELF_USER_STORAGE_KEY);
   localStorage.removeItem(ACCESS_GROUPS_STORAGE_KEY);
   setUserRoles([]);
   localStorage.removeItem(USER_ROLES_STORAGE_KEY);
   return;
  }

  if (selfUser) {
   return;
  }
  dispatch(usersGetSelfStart({}));
 }, [dispatch, loginUser, selfUser]);

 useEffect(() => {
  if (!loginUser) {
   return;
  }

  const storedAccessGroups = localStorage.getItem(ACCESS_GROUPS_STORAGE_KEY);

  if (storedAccessGroups) {
   try {
    const parsedAccessGroups = JSON.parse(storedAccessGroups) as (
     | GroupWithRolesDTO
     | AccessGroupsDTO
    )[];

    const parsedUserRoles = getUserRolesFromAccessGroups(parsedAccessGroups);
    setUserRoles(parsedUserRoles);
    localStorage.setItem(
     USER_ROLES_STORAGE_KEY,
     JSON.stringify(parsedUserRoles),
    );

    return;
   } catch {
    localStorage.removeItem(ACCESS_GROUPS_STORAGE_KEY);
   }
  }

  dispatch(usersGetSelfAccessGroupsStart());
 }, [dispatch, loginUser]);

 useEffect(() => {
  if (!success?.usersGetSelf || !user) {
   return;
  }

  setSelfUser(user);
  localStorage.setItem(SELF_USER_STORAGE_KEY, JSON.stringify(user));
 }, [success?.usersGetSelf, user]);

 useEffect(() => {
  if (!success?.usersGetSelfAccessGroups || !accessGroups) {
   return;
  }

  localStorage.setItem(ACCESS_GROUPS_STORAGE_KEY, JSON.stringify(accessGroups));

  const parsedUserRoles = getUserRolesFromAccessGroups(accessGroups);
  setUserRoles(parsedUserRoles);
  localStorage.setItem(USER_ROLES_STORAGE_KEY, JSON.stringify(parsedUserRoles));
 }, [accessGroups, success?.usersGetSelfAccessGroups]);

 const value = useMemo(
  () => ({
   loginUser,
   selfUser,
   isAuthenticated: Boolean(loginUser),
   userRoles,
   hasRole,
   databases:
    getDatabasesFromSelfUser(selfUser) || getDatabasesFromLoginUser(loginUser),
  }),
  [loginUser, selfUser, userRoles, hasRole],
 );

 return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
 const context = useContext(AuthContext);
 if (!context) {
  throw new Error('useAuthContext must be used within an AuthProvider');
 }
 return context;
};

